import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  GET_SUCCESSFUL_PARTICIPATIONS_FOR_PAYBACK,
  COMPLETE_PARTICIPATION_AND_PAYBACK,
} from "../api/review";
import {
  MissionAdminCompleteParticipationAndPaybackMutation,
  MissionAdminCompleteParticipationAndPaybackMutationVariables,
} from "../../../../__generated__/graphql";

// 타입 정의
type PaybackItem = {
  participationId: number;
  approvedStepCount: number;
  totalStepCount: number;
  participation: {
    id: number;
    userId: number;
    status: string;
    joinedAt: string;
    depositAmount: number;
    prizeAmount: number;
    user: {
      id: number;
      name: string | null;
      phone: string | null;
    };
    run: {
      id: number;
      startAt: string;
      endAt: string;
      campaign: {
        id: number;
        productName: string;
        productImageUrl: string | null;
        brand: {
          name: string;
        };
        steps: Array<{
          id: number;
          stepOrder: number;
          title: string | null;
        }>;
      };
    };
  };
};

interface UserGroup {
  user: PaybackItem["participation"]["user"];
  items: PaybackItem[];
}

interface CampaignGroup {
  campaign: PaybackItem["participation"]["run"]["campaign"];
  users: UserGroup[];
}

const SuccessesList = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<{
    campaignId: number | null;
    userId: number | null;
  }>({
    campaignId: null,
    userId: null,
  });
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  const { data, loading, error, refetch } = useQuery(
    GET_SUCCESSFUL_PARTICIPATIONS_FOR_PAYBACK,
    {
      variables: {
        filter: {
          campaignId: filter.campaignId || undefined,
          userId: filter.userId || undefined,
        },
      },
    },
  );

  const [completeParticipation, { loading: processing }] = useMutation<
    MissionAdminCompleteParticipationAndPaybackMutation,
    MissionAdminCompleteParticipationAndPaybackMutationVariables
  >(COMPLETE_PARTICIPATION_AND_PAYBACK, {
    onCompleted: () => {
      if (!isBatchProcessing) {
        alert("성공 처리 및 페이백이 완료되었습니다.");
        setProcessingId(null);
        refetch();
      }
    },
    onError: (error) => {
      if (!isBatchProcessing) {
        alert(`오류: ${error.message}`);
        setProcessingId(null);
      }
    },
  });

  // 일괄 처리용 뮤테이션 (콜백 없이 사용)
  const [batchCompleteParticipation] = useMutation<
    MissionAdminCompleteParticipationAndPaybackMutation,
    MissionAdminCompleteParticipationAndPaybackMutationVariables
  >(COMPLETE_PARTICIPATION_AND_PAYBACK);

  const handleSuccess = (participationId: number) => {
    if (!window.confirm("이 참여를 성공 처리하고 페이백을 진행하시겠습니까?")) {
      return;
    }

    setProcessingId(participationId);
    completeParticipation({
      variables: {
        participationId,
      },
    });
  };

  // 편집 모드 토글
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    if (isEditMode) {
      setSelectedIds(new Set());
    }
  };

  // 개별 선택 토글
  const toggleSelection = (participationId: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(participationId)) {
      newSelected.delete(participationId);
    } else {
      newSelected.add(participationId);
    }
    setSelectedIds(newSelected);
  };

  // 전체 선택/해제
  const toggleSelectAll = () => {
    const items = (data?.missionAdminSuccessfulParticipationsForPayback ||
      []) as PaybackItem[];
    const allIds = new Set(items.map((item) => item.participationId));

    if (selectedIds.size === allIds.size) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(allIds);
    }
  };

  // 일괄 승인 처리
  const handleBatchSuccess = async () => {
    if (selectedIds.size === 0) {
      alert("선택된 항목이 없습니다.");
      return;
    }

    if (
      !window.confirm(
        `선택한 ${selectedIds.size}개의 참여를 모두 성공 처리하고 페이백을 진행하시겠습니까?`,
      )
    ) {
      return;
    }

    setIsBatchProcessing(true);
    const idsArray = Array.from(selectedIds);
    const errors: string[] = [];

    try {
      // 순차적으로 처리하여 서버 부하 방지
      for (let i = 0; i < idsArray.length; i++) {
        const participationId = idsArray[i];
        try {
          const result = await batchCompleteParticipation({
            variables: {
              participationId,
            },
          });
          if (result.data?.missionAdminCompleteParticipationAndPayback.error) {
            errors.push(
              `ID ${participationId}: ${result.data.missionAdminCompleteParticipationAndPayback.error}`,
            );
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "알 수 없는 오류";
          errors.push(`ID ${participationId}: ${errorMessage}`);
        }
      }

      if (errors.length > 0) {
        alert(`일부 항목 처리 중 오류가 발생했습니다:\n${errors.join("\n")}`);
      } else {
        alert(`성공적으로 ${idsArray.length}개의 항목을 처리했습니다.`);
      }

      setSelectedIds(new Set());
      setIsEditMode(false);
      refetch();
    } catch (error) {
      alert(
        `일괄 처리 중 오류가 발생했습니다: ${error instanceof Error ? error.message : "알 수 없는 오류"}`,
      );
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("ko-KR", {
      style: "currency",
      currency: "KRW",
    }).format(amount);
  };

  // 캠페인별로 그룹핑하고, 각 캠페인 내에서 유저별로 그룹핑
  const groupedData = useMemo((): CampaignGroup[] => {
    const items = (data?.missionAdminSuccessfulParticipationsForPayback ||
      []) as PaybackItem[];

    // 1단계: 캠페인별로 그룹핑
    const campaignMap = new Map<
      number,
      {
        campaign: PaybackItem["participation"]["run"]["campaign"];
        users: Map<number, UserGroup>;
      }
    >();

    items.forEach((item) => {
      const campaignId = item.participation.run.campaign.id;
      const campaign = item.participation.run.campaign;

      if (!campaignMap.has(campaignId)) {
        campaignMap.set(campaignId, {
          campaign,
          users: new Map(),
        });
      }

      const campaignData = campaignMap.get(campaignId);
      if (!campaignData) return;

      const userId = item.participation.user.id;
      const user = item.participation.user;

      if (!campaignData.users.has(userId)) {
        campaignData.users.set(userId, {
          user,
          items: [],
        });
      }

      const userGroup = campaignData.users.get(userId);
      if (userGroup) {
        userGroup.items.push(item);
      }
    });

    // Map을 배열로 변환
    const result: CampaignGroup[] = Array.from(campaignMap.entries()).map(
      ([, data]) => {
        const users: UserGroup[] = Array.from(data.users.entries()).map(
          ([, userData]) => userData,
        );

        return {
          campaign: data.campaign,
          users,
        };
      },
    );

    return result;
  }, [data]);

  // 모든 항목의 ID 추출
  const allItems = useMemo(() => {
    return (data?.missionAdminSuccessfulParticipationsForPayback ||
      []) as PaybackItem[];
  }, [data]);

  const allIds = useMemo(() => {
    return new Set(allItems.map((item) => item.participationId));
  }, [allItems]);

  const isAllSelected = allIds.size > 0 && selectedIds.size === allIds.size;
  const isIndeterminate =
    selectedIds.size > 0 && selectedIds.size < allIds.size;

  if (loading) return <div className="text-center py-8">로딩 중...</div>;
  if (error)
    return (
      <div className="text-center py-8 text-red-600">에러: {error.message}</div>
    );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">미션 성공처리</h1>
      </div>

      {/* 필터 */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              캠페인 ID
            </label>
            <input
              type="number"
              value={filter.campaignId || ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  campaignId: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="캠페인 ID 입력"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              유저 ID
            </label>
            <input
              type="number"
              value={filter.userId || ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  userId: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="유저 ID 입력"
            />
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={() => refetch()}
            className="bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700"
          >
            검색
          </button>
        </div>
      </div>

      {/* 선택 및 일괄 승인 버튼 */}
      <div className="flex justify-end items-center mb-6">
        <div className="flex gap-2">
          {isEditMode && (
            <button
              onClick={handleBatchSuccess}
              disabled={isBatchProcessing || selectedIds.size === 0}
              className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBatchProcessing
                ? `처리 중... (${selectedIds.size}개)`
                : `일괄 승인 (${selectedIds.size}개)`}
            </button>
          )}
          <button
            onClick={toggleEditMode}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${
              isEditMode
                ? "bg-gray-600 text-white hover:bg-gray-700"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {isEditMode ? "선택 취소" : "선택"}
          </button>
        </div>
      </div>

      {/* 캠페인별 그룹 */}
      {groupedData.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          성공 처리 대상이 없습니다.
        </div>
      ) : (
        <div className="space-y-6">
          {groupedData.map((campaignGroup) => {
            const { campaign, users } = campaignGroup;

            return (
              <div
                key={campaign.id}
                className="bg-white rounded-lg shadow overflow-hidden"
              >
                {/* 캠페인 헤더 */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-4">
                    {campaign.productImageUrl && (
                      <img
                        src={campaign.productImageUrl}
                        alt={campaign.productName}
                        className="w-12 h-12 object-cover rounded"
                      />
                    )}
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {campaign.productName}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {campaign.brand.name}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 테이블 */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {isEditMode && (
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                            <input
                              type="checkbox"
                              checked={isAllSelected}
                              ref={(input) => {
                                if (input)
                                  input.indeterminate = isIndeterminate;
                              }}
                              onChange={toggleSelectAll}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                          </th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          유저이름
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          번호
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          예치금
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          페이백 금액
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          승인된 미션
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          액션
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((userGroup) => {
                        const { user, items } = userGroup;
                        // 한 유저가 같은 캠페인에 여러 참여를 할 수 있으므로 첫 번째 항목 사용
                        const item = items[0];
                        const isProcessing =
                          processingId === item.participationId;

                        return (
                          <tr
                            key={user.id}
                            className={`hover:bg-gray-50 ${
                              isEditMode ? "" : "cursor-pointer"
                            } ${selectedIds.has(item.participationId) ? "bg-blue-50" : ""}`}
                            onClick={() => {
                              if (!isEditMode) {
                                navigate(
                                  `/ops/mission/reviews/submissions/${item.participationId}`,
                                );
                              }
                            }}
                          >
                            {isEditMode && (
                              <td
                                className="px-4 py-3 text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(
                                    item.participationId,
                                  )}
                                  onChange={() =>
                                    toggleSelection(item.participationId)
                                  }
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                />
                              </td>
                            )}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name || `유저 ${user.id}`}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {user.phone || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(
                                  item.participation.depositAmount,
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-emerald-600">
                                {formatCurrency(item.participation.prizeAmount)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="text-sm text-emerald-600 font-medium">
                                {item.approvedStepCount}/{item.totalStepCount}개
                              </div>
                            </td>
                            <td
                              className="px-4 py-3 text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() =>
                                  handleSuccess(item.participationId)
                                }
                                disabled={processing}
                                className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isProcessing ? "처리 중..." : "성공 처리"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SuccessesList;
